import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown
} from "reactstrap";

import {
  getOverlapStatusLabel,
  getOverlapStatusShortLabel,
  isConfirmedDuplicate,
} from "common/constants/overlap-status";
import {
  buildListParams,
  parseFilter,
  parsePage,
  parsePageSize,
  parseSearch,
  parseSort,
} from "common/lib/listUrlParams";
import { getSortObj } from "common/lib/tableDataOptions";
import {
  exportOverlappingPaymentsToCsv,
  getOverlappingPayments,
  getOverlappingPaymentsSyncStatus,
  startOverlappingPaymentsSync,
} from "common/services/payments.service";
import TableContainer from "../../Components/Common/TableContainer";

import Permission from "Components/Common/Permission";
import Permissions from "Components/Common/Permissions";
// import PositionTypeFilter from "./PositionTypeFilter";
// import AgencyFilter from "./AgencyFilter";
import BreadCrumb from "Components/Common/BreadCrumb";
import BulkActionBar from "Components/Common/BulkActionBar";
import ExportLoadingModal from "Components/Common/ExportLoadingModal";
import { useProfile } from "Components/Hooks/UserHooks";
import { normalize } from "common/lib/normalize";
import { isAllowed } from "common/lib/permissionUtils";
import * as moment from "moment";
import { toast } from "react-toastify";
import BulkResolvePayment from "./BulkResolvePayment";
import Filters from "./Filters";
import ResolvePaymentModal from "./ResolvePayment";
import UpdatePayment from "./UpdatePayment";

interface DateRange {
  fromDate: Date | null;
  toDate: Date | null;
}

interface FilterValue {
  positionType: string;
  agency: string;
  dateRange: DateRange;
  region: string;
  mda: string;
  paymentCategory: string;
  thematicArea: string;
  sector: string;
  resolved?: string;
}

const defaultSort = [{ id: "createdAt", desc: true }];

/** How often the client asks the server how far the background sync has got. */
const SYNC_POLL_INTERVAL_MS = 1500;

const defaultFilterValue: FilterValue = {
  positionType: "",
  agency: "",
  dateRange: {
    fromDate: null,
    toDate: null,
  },
  region: "",
  mda: "",
  paymentCategory: "",
  thematicArea: "",
  sector: "",
  resolved: "Unresolved",
};

const Payments = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [params] = useState({
    page: parsePage(searchParams),
    pageSize: parsePageSize(searchParams),
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  // Update pageIndex
  const setPage = (newPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: newPage - 1,
    }));
  };

  // Update pageSize
  // const setPageSize = (newPageSize: number) => {
  //   setPagination((prevPagination) => ({
  //     ...prevPagination,
  //     pageSize: newPageSize,
  //   }));
  // };

  const [sorted, setSorted] = useState(() => parseSort(searchParams.get("sort"), defaultSort));

  const [selectedRecord, setSelectedRecord] = useState<any>({});

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const showUpdateModalForm = (data: any) => {
    setSelectedRecord(data);
    setIsUpdateModalOpen(true);
  };

  const [searchValue, setSearchValue] = useState(parseSearch(searchParams));
  
  const [filterValue, setFilterValue] = useState(() => parseFilter(searchParams, defaultFilterValue));

  const unCheckAll = useCallback(() => {
    const checkall: any = document.getElementById("checkBoxAll");
    if (checkall) checkall.checked = false;
    setSelectedCheckBox([]);
  }, []);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});
  const [apiParams, setApiParams] = useState<any>({});

  // Reuses the filters/sort the table was last fetched with, so the export
  // always mirrors what the user is looking at.
  const [exporting, setExporting] = useState(false);
  const getCsvExport = useCallback(async () => {
    setExporting(true);
    await exportOverlappingPaymentsToCsv({
      filters: apiParams?.filters,
      sort: apiParams?.sort,
    })
      .then((response: any) => {
        const objectUrl = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = objectUrl;
        link.setAttribute("download", "Overlapping_Payments_Export.csv");
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
      })
      .catch(() => {
        toast.error("An error occurred while exporting overlapping payments");
      });
    setExporting(false);
  }, [apiParams]);

  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: any;
    }) => {
      const params = buildListParams(searchParams, {
        page,
        pageSize: pagination.pageSize,
        sorted,
        searchValue,
        filterValue,
        defaultFilterValue,
        defaultSort,
      });
      setSearchParams(params, {
        replace: true,
      });

      const sortObj = getSortObj(sorted);

      let andConditions = [];

      if (filterValue?.agency) {
        andConditions.push({
          $or: [
            {
              payment: {
                agency: {
                  id: {
                    $eq: filterValue?.agency.value,
                  },
                },
              },
            },
            {
              overlappingPayment: {
                agency: {
                  id: {
                    $eq: filterValue?.agency.value,
                  },
                },
              },
            },
          ],
        });
      }
      if (filterValue?.region) {
        andConditions.push({
          $or: [
            {
              payment: {
                mda: {
                  region: {
                    id: {
                      $eq: filterValue?.region.value,
                    },
                  },
                },
              },
            },
            {
              overlappingPayment: {
                mda: {
                  region: {
                    id: {
                      $eq: filterValue?.region.value,
                    },
                  },
                },
              },
            },
          ],
        });
      }
      if (filterValue?.mda) {
        andConditions.push({
          $or: [
            {
              payment: {
                mda: {
                  id: {
                    $eq: filterValue?.mda.value,
                  },
                },
              },
            },
            {
              overlappingPayment: {
                mda: {
                  id: {
                    $eq: filterValue?.mda.value,
                  },
                },
              },
            },
          ],
        });
      }
      if (filterValue?.paymentCategory) {
        andConditions.push({
          $or: [
            {
              payment: {
                paymentCategory: {
                  id: {
                    $eq: filterValue?.paymentCategory.value,
                  },
                },
              },
            },
            {
              overlappingPayment: {
                paymentCategory: {
                  id: {
                    $eq: filterValue?.paymentCategory.value,
                  },
                },
              },
            },
          ],
        });
      }

      if (filterValue?.positionType) {
        andConditions.push({
          $or: [
            {
              payment: {
                positionType: {
                  id: {
                    $eq: filterValue.positionType.value,
                  },
                },
              },
            },
            {
              overlappingPayment: {
                positionType: {
                  id: {
                    $eq: filterValue.positionType.value,
                  },
                },
              },
            },
          ],
        });
      }

      if (filterValue?.resolved === "Resolved") {
        andConditions.push({
          resolved: true,
        });
      } else if (filterValue?.resolved === "Unresolved") {
        andConditions.push({
          resolved: false,
        });
      } else if (filterValue?.resolved === "All") {
        andConditions.push({
          resolved: {
            $in: [true, false, null],
          },
        });
      }

      andConditions.push({
        $or: [
          {
            payment: {
              individual: {
                fullName: {
                  $containsi: searchValue,
                },
              },
            },
          },
          {
            overlappingPayment: {
              individual: {
                fullName: {
                  $containsi: searchValue,
                },
              },
            },
          },
          {
            payment: {
              positionTitle: {
                $containsi: searchValue,
              },
            },
          },
          {
            overlappingPayment: {
              positionTitle: {
                $containsi: searchValue,
              },
            },
          },
          {
            payment: {
              projectOrFund: {
                $containsi: searchValue,
              },
            },
          },
          {
            overlappingPayment: {
              projectOrFund: {
                $containsi: searchValue,
              },
            },
          },
        ],
      });

      try {
        setLoading(true);
        unCheckAll();
        const APIParams = {
          pagination: {
            page,
            pageSize: pagination.pageSize,
          },
          sort: sortObj,
          populate: [
            "payment",
            "payment.agency",
            "payment.mda",
            "payment.mda.region",
            "payment.individual",
            "overlappingPayment",
            "overlappingPayment.agency",
            "overlappingPayment.mda",
            "overlappingPayment.mda.region",
            "overlappingPayment.individual",
          ],
          filters: {
            $and: andConditions,
          },
        };
        setApiParams(APIParams);
        const response: any = await getOverlappingPayments(APIParams);
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {});
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, searchParams, setSearchParams, unCheckAll]
  );

  const fetchUpdatedData = useCallback(() => {
    onPageChange({
      page: pagination.pageIndex + 1,
      sorted: sorted,
      searchValue: searchValue,
      filterValue: filterValue,
    });
  }, [onPageChange, pagination.pageIndex, sorted, searchValue, filterValue]);

  const checkedAll = useCallback(() => {
    const checkall: any = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".tableCheckbox");

    if (checkall.checked) {
      ele.forEach((ele: any) => {
        ele.checked = true;
      });
    } else {
      ele.forEach((ele: any) => {
        ele.checked = false;
      });
    }
    onSelectCheckbox();
  }, []);

  const [selectedCheckBox, setSelectedCheckBox] = useState<any>([]);

  const onSelectCheckbox = () => {
    const ele = document.querySelectorAll(".tableCheckbox:checked");
    setSelectedCheckBox(ele);
  };
  /* 
  Resolve Payment
  */
  const [resolvePaymentModal, setResolvePaymentModal] = useState(false);
  const handleResolve = (data: any) => {
    setResolvePaymentModal(true);
    setSelectedRecord(data);
  };

  // "resolve" targets the unresolved rows in the selection, "edit" the
  // already-resolved ones; null keeps the shared modal closed.
  const [bulkMode, setBulkMode] = useState<"resolve" | "edit" | null>(null);

  const selectedIds = useMemo(
    () => Array.from(selectedCheckBox).map((ele: any) => String(ele.value)),
    [selectedCheckBox]
  );

  // Selection is cleared on every fetch, so the checked rows are always part of
  // the page currently in `data` and their resolved flag can be read from it.
  const selectedRows = useMemo(
    () => (data || []).filter((row: any) => selectedIds.includes(String(row.id))),
    [data, selectedIds]
  );

  const unresolvedSelectedIds = useMemo(
    () =>
      selectedRows
        .filter((row: any) => row?.resolved !== true)
        .map((row: any) => String(row.id)),
    [selectedRows]
  );

  const resolvedSelectedIds = useMemo(
    () =>
      selectedRows
        .filter((row: any) => row?.resolved === true)
        .map((row: any) => String(row.id)),
    [selectedRows]
  );

  const bulkEligibleIds =
    bulkMode === "edit" ? resolvedSelectedIds : unresolvedSelectedIds;

  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<{
    progress: number;
    phase: string;
  } | null>(null);
  const pollTimer = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      window.clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  // Don't keep polling after the user navigates away mid-sync.
  useEffect(() => stopPolling, [stopPolling]);

  const pollSyncStatus = useCallback(
    async (jobId: string) => {
      try {
        const response: any = await getOverlappingPaymentsSyncStatus(jobId);
        const job = response?.job;
        if (!job) throw new Error("Malformed sync status response");

        setSyncStatus({ progress: job.progress ?? 0, phase: job.phase ?? "" });

        if (job.status === "running") {
          pollTimer.current = window.setTimeout(
            () => pollSyncStatus(jobId),
            SYNC_POLL_INTERVAL_MS
          );
          return;
        }

        stopPolling();
        setSyncing(false);
        setSyncStatus(null);

        if (job.status === "completed") {
          fetchUpdatedData();
          const pairs = job.result?.overlapping_pairs_found;
          toast.success(
            typeof pairs === "number"
              ? `Sync complete — ${pairs} overlapping pair${
                  pairs === 1 ? "" : "s"
                } found`
              : "Overlapping payments synced successfully"
          );
        } else {
          toast.error(job.error || "Error while syncing overlapping payments");
        }
      } catch (e: any) {
        // A restarted backend drops the job, so stop rather than poll forever.
        stopPolling();
        setSyncing(false);
        setSyncStatus(null);
        toast.error(
          "Lost track of the sync job. Refresh the page to see the latest results."
        );
      }
    },
    [fetchUpdatedData, stopPolling]
  );

  const handleSyncOverlappingPayments = async () => {
    if (syncing) return;
    setSyncing(true);
    setSyncStatus({ progress: 0, phase: "Starting" });
    try {
      const response: any = await startOverlappingPaymentsSync();
      const job = response?.job;
      if (!job?.id) throw new Error("Malformed sync start response");

      if (response.alreadyRunning) {
        toast.info("A sync is already running — tracking its progress");
      }
      pollSyncStatus(job.id);
    } catch (e: any) {
      console.log("Error", e);
      setSyncing(false);
      setSyncStatus(null);
      toast.error("Error while starting the overlapping payments sync");
    }
  };

  const { userProfile } = useProfile();
  const [columnVisibility] = useState({
    checkBoxColumn: isAllowed(userProfile?.permissions, [
      {
        resource: "overlapping-payment",
        action: "update",
      },
    ]),
    resolved: isAllowed(userProfile?.permissions, [
      {
        resource: "overlapping-payment",
        action: "update",
      },
    ]),
    overlapStatus: isAllowed(userProfile?.permissions, [
      {
        resource: "overlapping-payment",
        action: "update",
      },
    ]),
    updatedAt: isAllowed(userProfile?.permissions, [
      {
        resource: "overlapping-payment",
        action: "update",
      },
    ]),
    "payment.createdAt": isAllowed(userProfile?.permissions, [
      {
        resource: "overlapping-payment",
        action: "create",
      },
    ]),
  });

  const columns = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            id="checkBoxAll"
            className="form-check-input"
            onClick={() => checkedAll()}
          />
        ),
        cell: (cell: any) => {
          return (
            <input
              type="checkbox"
              className="tableCheckbox form-check-input"
              value={cell.getValue()}
              onChange={() => onSelectCheckbox()}
            />
          );
        },
        id: "checkBoxColumn",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Ministry/Department/Agency (MDA)",
        accessorKey: "payment.mda.name",
        id: "payment.mda.name",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;
          return (
            <>
              <div
                className="d-flex align-items-center"
                style={{
                  whiteSpace: "pre-wrap",
                  // maxWidth, not width: a fixed width pinned the column at
                  // 400px even when the name only needed ~200px, leaving a
                  // large gap before the Agency column. This still wraps long
                  // MDA names at 400px.
                  maxWidth: "400px",
                }}
              >
                <div className="flex-grow-1">
                  {/* Dots mirror the legend above the table so each line is
                      identifiable as the first or the overlapping payment. */}
                  <h5 className="fs-14 mb-2">
                    <i
                      className="ri-checkbox-blank-circle-fill fs-10 me-2 align-middle text-body"
                      title="First payment"
                    ></i>
                    {/* Plain text, not a link: the whole row navigates to the
                        pair's detail page. */}
                    <span className="text-body">
                      {`${rowData?.payment?.mda?.name} - ${rowData?.payment?.mda?.region?.name}`}
                      {rowData?.payment?.mda?.acronym
                        ? ` (${rowData?.payment?.mda?.acronym})`
                        : ""}
                    </span>
                  </h5>
                  <h5 className="fs-14 mb-1 text-muted">
                    <i
                      className="ri-checkbox-blank-circle-fill fs-10 me-2 align-middle"
                      title="Overlapping payment"
                    ></i>
                    <span className="text-muted">
                      {`${rowData?.overlappingPayment?.mda?.name} - ${rowData?.overlappingPayment?.mda?.region?.name}`}
                      {rowData?.overlappingPayment?.mda?.acronym
                        ? ` (${rowData?.overlappingPayment?.mda?.acronym})`
                        : ""}
                    </span>
                  </h5>
                </div>
              </div>
            </>
          );
        },
      },

      {
        header: "Agency",
        accessorKey: "payment.agency.name",
        enableColumnFilter: false,
        id: "payment.agency.name",
        cell: (cell: any) => (
          <div
            className="d-flex align-items-center"
            style={{
              whiteSpace: "pre-wrap",
              width: "100px",
            }}
          >
            <div className="flex-grow-1">
              <div className="fs-14 mb-1">
                {cell.row.original?.payment?.agency?.name || "Not Provided"}
              </div>
              <div className="fs-14 mb-1 text-muted">
                {cell.row.original?.overlappingPayment?.agency?.name ||
                  "Not Provided"}
              </div>
            </div>
          </div>
        ),
      },

      {
        header: "Individual Name",
        accessorKey: "payment.individual.fullName",
        enableColumnFilter: false,
        id: "payment.individual.fullName",
        cell: (cell: any) => (
          <div
            className="d-flex align-items-center"
            style={{
              whiteSpace: "pre-wrap",
              width: "200px",
            }}
          >
            <div className="flex-grow-1">
              <div className="fs-14 mb-1 text-body">
                {cell.row.original?.payment?.individual?.fullName ||
                  "Not Provided"}
              </div>
              <div className="fs-14 mb-1 text-muted">
                {cell.row.original?.overlappingPayment?.individual?.fullName ||
                  "Not Provided"}
              </div>
            </div>
          </div>
        ),
      },

      {
        header: "Position Title",
        accessorKey: "payment.positionTitle",
        enableColumnFilter: false,
        id: "payment.positionTitle",
        cell: (cell: any) => (
          <div
            className="d-flex align-items-center"
            style={{
              whiteSpace: "pre-wrap",
              width: "200px",
            }}
          >
            <div className="flex-grow-1">
              <div className="fs-14 mb-1">
                {cell.row.original?.payment?.positionTitle || "Not Provided"}
              </div>
              <div className="fs-14 mb-1 text-muted">
                {cell.row.original?.overlappingPayment?.positionTitle ||
                  "Not Provided"}
              </div>
            </div>
          </div>
        ),
      },

      {
        header: "Start Date",
        accessorKey: "payment.startDate",
        id: "payment.startDate",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;
          if (rowData?.payment?.startDate === null) return null;
          const paymentDate = moment(
            new Date(rowData?.payment?.startDate)
          ).format("DD MMM Y");
          const overlappingPaymentDate = moment(
            new Date(rowData?.overlappingPayment?.startDate)
          ).format("DD MMM Y");
          return (
            <div
              className="d-flex align-items-center"
              style={{
                whiteSpace: "pre-wrap",
                width: "100px",
              }}
            >
              <div className="flex-grow-1">
                <div className="fs-14 mb-1">
                  {(rowData?.payment?.startDate && paymentDate) ||
                    "Not Provided"}
                </div>
                <div className="fs-14 mb-1 text-muted">
                  {(rowData?.overlappingPayment?.startDate &&
                    overlappingPaymentDate) ||
                    "Not Provided"}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        header: "End Date",
        accessorKey: "payment.endDate",
        id: "payment.endDate",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;
          if (rowData?.payment?.endDate === null) return null;
          const paymentDate = moment(
            new Date(rowData?.payment?.endDate)
          ).format("DD MMM Y");
          const overlappingPaymentDate = moment(
            new Date(rowData?.overlappingPayment?.endDate)
          ).format("DD MMM Y");
          return (
            <div
              className="d-flex align-items-center"
              style={{
                whiteSpace: "pre-wrap",
                width: "100px",
              }}
            >
              <div className="flex-grow-1">
                <div className="fs-14 mb-1">
                  {(rowData?.payment?.endDate && paymentDate) || "Not Provided"}
                </div>
                <div className="fs-14 mb-1 text-muted">
                  {(rowData?.overlappingPayment?.endDate &&
                    overlappingPaymentDate) ||
                    "Not Provided"}
                </div>
              </div>
            </div>
          );
        },
      },

      {
        header: "Amount / Month",
        accessorKey: "payment.amountPerMonth",
        enableColumnFilter: false,
        id: "payment.amountPerMonth",
        cell: (cell: any) => {
          const rowData = cell.row.original;
          const paymentAmount = rowData?.payment?.amountPerMonth;
          const overlappingPaymentAmount =
            rowData?.overlappingPayment?.amountPerMonth;
          return (
            <div
              className="d-flex align-items-center"
              style={{
                whiteSpace: "pre-wrap",
                width: "100px",
              }}
            >
              <div className="flex-grow-1">
                <div className="fs-14 mb-1">
                  {(paymentAmount && `$ ${paymentAmount.toLocaleString()}`) ||
                    "Not Provided"}
                </div>
                <div className="fs-14 mb-1 text-muted">
                  {(overlappingPaymentAmount &&
                    `$ ${overlappingPaymentAmount.toLocaleString()}`) ||
                    "Not Provided"}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        header: "Resolved?",
        accessorKey: "resolved",
        id: "resolved",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const resolved = cell.row.original.resolved;
          return (
            <React.Fragment>
              {resolved === null ? (
                <span className="badge bg-warning-subtle text-warning fs-13">
                  Not Provided
                </span>
              ) : resolved ? (
                <span className="badge bg-success-subtle text-success fs-13">
                  Resolved
                </span>
              ) : resolved === false ? (
                <span className="badge bg-danger-subtle text-danger fs-13">
                  Unresolved
                </span>
              ) : null}
            </React.Fragment>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "overlapStatus",
        id: "overlapStatus",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const status = cell.row.original.overlapStatus;
          if (!status)
            return (
              <span className="badge bg-secondary-subtle text-secondary fs-13">
                Not Set
              </span>
            );
          return (
            // Short label keeps the column narrow; the full wording stays
            // available on hover and in the view/edit modals.
            <span
              className={`badge fs-13 ${
                isConfirmedDuplicate(status)
                  ? "bg-danger-subtle text-danger"
                  : "bg-success-subtle text-success"
              }`}
              title={getOverlapStatusLabel(status)}
            >
              {getOverlapStatusShortLabel(status)}
            </span>
          );
        },
      },

      {
        header: "Created At",
        accessorKey: "payment.createdAt",
        id: "payment.createdAt",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;
          const paymentDate = moment(
            new Date(rowData?.payment?.createdAt)
          ).format("DD MMM Y");
          const overlappingPaymentDate = moment(
            new Date(rowData?.overlappingPayment?.createdAt)
          ).format("DD MMM Y");

          return (
            <div
              className="d-flex align-items-center"
              style={{
                whiteSpace: "pre-wrap",
                width: "100px",
              }}
            >
              <div className="flex-grow-1">
                <div className="fs-14 mb-1">
                  {paymentDate || "Not Provided"}
                </div>
                <div className="fs-14 mb-1 text-muted">
                  {overlappingPaymentDate || "Not Provided"}
                </div>
              </div>
            </div>
          );
        },
      },

      {
        header: "Action",
        enableSorting: false,
        cell: (cellProps: any) => {
          const rowData = cellProps.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                href="#"
                className="btn btn-soft-secondary btn-sm dropdown"
                tag="button"
              >
                <i className="ri-more-fill align-middle"></i>
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <Permission resource="overlapping-payment" action="findOne">
                  <DropdownItem
                    href={`/overlapping-payments/${rowData?.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/overlapping-payments/${rowData?.id}`);
                    }}
                  >
                    <i className="ri-eye-fill align-bottom me-2 text-muted"></i>{" "}
                    View Payment
                  </DropdownItem>
                </Permission>
                {/* A record is either resolved or not, so it gets exactly one
                    of these two actions — never both. */}
                {rowData?.resolved === true ? (
                  <Permission resource="overlapping-payment" action="update">
                    <DropdownItem
                      href="/#"
                      onClick={(e) => {
                        e.preventDefault();
                        showUpdateModalForm(rowData);
                      }}
                    >
                      <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>{" "}
                      Edit Resolution
                    </DropdownItem>
                  </Permission>
                ) : (
                  <Permission resource="overlapping-payment" action="update">
                    <DropdownItem
                      href="/#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleResolve(rowData);
                      }}
                    >
                      <i className="ri-checkbox-circle-fill align-bottom me-2 text-muted"></i>{" "}
                      Resolve Payment
                    </DropdownItem>
                  </Permission>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [checkedAll, navigate]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Overlapping Payments"
            pageTitles={[{ title: "Dashboard", link: "/dashboard" }]}
          />

          <Filters
            setSearchValue={setSearchValue}
            searchValue={searchValue}
            setFilterValue={setFilterValue}
            filterValue={filterValue}
            setPage={setPage}
            setSorted={setSorted}
            defaultFilterValue={defaultFilterValue}
          />

          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={6}>
                  <h5 className="card-title mb-1">
                    Overlapping Payments
                    {meta?.pagination?.total ? (
                      <span className="badge bg-primary-subtle text-primary fs-12 ms-2 align-middle">
                        {meta.pagination.total.toLocaleString()} pair
                        {meta.pagination.total === 1 ? "" : "s"}
                      </span>
                    ) : null}
                  </h5>
                  <p className="mb-0 text-muted">
                    A list of overlapping payments and contracts.
                  </p>
                </Col>
                <div className="col-md-auto ms-auto">
                  <div className="d-flex gap-2 align-items-center">
                    <Permission resource="overlapping-payment" action="update">
                      <UncontrolledDropdown className="card-header-dropdown me-2">
                        <DropdownToggle
                          tag="a"
                          className="text-reset"
                          role="button"
                        >
                          <span className="fw-semibold text-uppercase fs-12 me-1">
                            Displaying :{" "}
                          </span>
                          <span className="text-muted">
                            {filterValue?.resolved === "All"
                              ? "All"
                              : filterValue?.resolved === "Resolved"
                              ? "Resolved"
                              : "Unresolved"}
                            <i className="mdi mdi-chevron-down ms-1"></i>
                          </span>
                        </DropdownToggle>
                        <DropdownMenu className="dropdown-menu-end">
                          <DropdownItem
                            onClick={() => {
                              setFilterValue({
                                ...filterValue,
                                resolved: "All",
                              });
                            }}
                          >
                            All
                          </DropdownItem>
                          <DropdownItem
                            onClick={() => {
                              setFilterValue({
                                ...filterValue,
                                resolved: "Resolved",
                              });
                            }}
                          >
                            Resolved
                          </DropdownItem>
                          <DropdownItem
                            onClick={() => {
                              setFilterValue({
                                ...filterValue,
                                resolved: "Unresolved",
                              });
                            }}
                          >
                            Unresolved
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </Permission>
                    <Permission resource="overlapping-payment" action="create">
                      <Link
                        to="#"
                        className={`btn btn-primary ${
                          syncing ? "disabled" : ""
                        }`}
                        aria-disabled={syncing}
                        onClick={(e) => {
                          e.preventDefault();
                          handleSyncOverlappingPayments();
                        }}
                      >
                        {syncing ? (
                          <i className="ri-loader-2-line animate-spin align-bottom me-1"></i>
                        ) : (
                          <i className="ri-refresh-line align-bottom me-1"></i>
                        )}
                        {syncing && syncStatus
                          ? `Syncing… ${syncStatus.progress}%`
                          : "Sync Overlapping Payments"}
                      </Link>
                    </Permission>
                    <Permission
                      resource="overlapping-payment"
                      action="exportToCsv"
                    >
                      <UncontrolledDropdown>
                        <DropdownToggle
                          tag="button"
                          className="btn btn-light"
                          id="overlappingPaymentsActionsDropdown"
                        >
                          <i className="ri ri-more-fill"></i>
                        </DropdownToggle>
                        <DropdownMenu className="dropdown-menu-end">
                          <DropdownItem
                            onClick={() => {
                              getCsvExport();
                            }}
                          >
                            <i className="ri ri-download-2-line me-1"></i>
                            Export
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </Permission>
                  </div>
                </div>
              </Row>
            </CardHeader>
            <CardBody>
              {/* Boxed so it reads as a key for the table rather than stray
                  text floating above the header row. */}
              {/* The table below carries `.table-card`, whose -16px margin-top
                  swallows 16px of whatever gap is set here. Adding that back on
                  top of the 16px we want leaves the space above and below the
                  legend equal to the card body's own 16px padding. */}
              <div
                className="d-flex justify-content-start"
                style={{ marginBottom: "32px" }}
              >
                <div className="d-inline-flex align-items-center flex-wrap gap-2 border rounded bg-light px-3 py-2 fs-12">
                  <span className="text-muted">Each row compares</span>
                  <span className="d-inline-flex align-items-center fw-medium">
                    <i className="ri-checkbox-blank-circle-fill fs-10 me-1"></i>
                    First payment
                  </span>
                  <span className="text-muted">vs</span>
                  <span className="d-inline-flex align-items-center text-muted">
                    <i className="ri-checkbox-blank-circle-fill fs-10 me-1"></i>
                    Overlapping payment
                  </span>
                </div>
              </div>
              <TableContainer
                loading={loading}
                columns={columns}
                data={data || []}
                pagination={pagination}
                setPagination={setPagination}
                sorted={sorted}
                setSorted={setSorted}
                onPageChange={onPageChange}
                FilterSection={() => {}}
                totalRows={meta?.pagination?.total || 0}
                totalPages={meta?.pagination?.pageCount || 0}
                searchValue={searchValue}
                filterValue={filterValue}
                columnVisibility={columnVisibility}
                onRowClick={(rowData: any) =>
                  navigate(`/overlapping-payments/${rowData?.id}`)
                }
                divClass="table-responsive table-card mb-4"
                tableClass="align-middle table-nowrap mb-0 table-striped"
                theadClass="table-light table-nowrap "
              />

              <ResolvePaymentModal
                toggle={() => setResolvePaymentModal(false)}
                isModalOpen={resolvePaymentModal}
                fetchData={fetchUpdatedData}
                selectedRecord={selectedRecord}
              />
              <UpdatePayment
                toggle={() => setIsUpdateModalOpen(false)}
                isModalOpen={isUpdateModalOpen}
                fetchData={fetchUpdatedData}
                selectedRecord={selectedRecord}
              />
              <ExportLoadingModal show={exporting} onCloseClick={() => {}} />
              <BulkResolvePayment
                toggle={() => setBulkMode(null)}
                isModalOpen={bulkMode !== null}
                mode={bulkMode ?? "resolve"}
                fetchData={fetchUpdatedData}
                eligibleIds={bulkEligibleIds}
                skippedCount={selectedIds.length - bulkEligibleIds.length}
                onCompleted={unCheckAll}
              />
            </CardBody>
          </Card>
        </Container>

        <Permissions
          checks={[{ resource: "overlapping-payment", action: "update" }]}
        >
          <BulkActionBar
            count={selectedCheckBox.length}
            onClear={unCheckAll}
            itemLabel="payment"
          >
            <Button
              color="primary"
              size="sm"
              className="material-shadow-none"
              disabled={unresolvedSelectedIds.length === 0}
              title={
                unresolvedSelectedIds.length === 0
                  ? "Every selected payment is already resolved"
                  : undefined
              }
              onClick={() => setBulkMode("resolve")}
            >
              <i className="ri-checkbox-circle-fill align-bottom me-1"></i>{" "}
              Resolve
            </Button>
            <Button
              color="secondary"
              size="sm"
              className="material-shadow-none"
              disabled={resolvedSelectedIds.length === 0}
              title={
                resolvedSelectedIds.length === 0
                  ? "None of the selected payments have been resolved yet"
                  : undefined
              }
              onClick={() => setBulkMode("edit")}
            >
              <i className="ri-pencil-fill align-bottom me-1"></i> Edit
              Resolution
            </Button>
          </BulkActionBar>
        </Permissions>
      </div>
    </React.Fragment>
  );
};

export default Payments;
